import { Button } from "@sellar-io/components";
import { useState } from "react";
import { Alert } from "../../../../../components/alert/Alert";
import { InputLabel } from "@sellar-io/components";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
} from "../../../../../components/table/TableLegacy";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { usePriceListList } from "../../../../../hooks/api/v2/priceList/usePriceListList";
import { useProductPriceListBatchCreate } from "../../../../../hooks/api/v2/productPriceList/useProductPriceListBatchCreate";
import ItemPricingTableRow from "./ItemPricingTableRow";
import { ProductPriceListBatchCreateRequestType } from "@sellar-io/api-v2/types";

export default function ItemPricingDialogContent({
  onDismiss,
  productId,
  onSubmit,
  loading,
  updatedPriceListItems,
  setUpdatedPriceListItems,
}: {
  loading: boolean;
  productId: number;
  onDismiss: (nextStep?: boolean) => void;
  onSubmit: () => void;
  updatedPriceListItems: ProductPriceListBatchCreateRequestType | undefined;
  setUpdatedPriceListItems: (
    item: ProductPriceListBatchCreateRequestType | undefined,
  ) => void;
}) {
  const [skipAttempts, setSkipAttempts] = useState<number>(0);
  const [warning, setWarning] = useState<boolean>(false);

  const productPriceListBatchCreateMutation = useProductPriceListBatchCreate();
  const priceListListQuery = usePriceListList({
    limit: 50,
    query: [
      {
        type: "ProductId",
        modifier: "EQUALS",
        value: productId,
      },
    ],
  });

  const closeDialog = () => {
    onDismiss(true);
    setSkipAttempts(0);
    setWarning(false);
  };

  const onConfirm = () => {
    if (!updatedPriceListItems) return closeDialog();
    onSubmit();
  };

  return (
    <div>
      <DialogContent>
        <DialogNavigation
          title="Item pricing"
          description="Set pricing for this item."
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => onDismiss()}
        />

        <div className={"flex flex-col gap-4"}>
          {productPriceListBatchCreateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(productPriceListBatchCreateMutation.error)
                  ?.response?.data.message ?? "Unknown error"
              }
            />
          ) : null}

          <div className="flex flex-col gap-2">
            <InputLabel>Pricing</InputLabel>
            {warning && (
              <Alert type={"warning"} title={"Pricing missing"} message={""}>
                <p>
                  Add pricing for <strong>at least one</strong> of your customer
                  groups to list this product on your storefront
                </p>
              </Alert>
            )}
            <TableLegacyContainer className={"max-h-[300px]"}>
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH className={"sticky top-0"}>
                      Price tier
                    </TableLegacyTH>
                    <TableLegacyTH className={"sticky top-0"}>
                      Unit price
                    </TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {priceListListQuery.data?.pages.map((page) =>
                    page.map((priceList) => (
                      <ItemPricingTableRow
                        key={priceList.id}
                        priceList={priceList}
                        setUpdatedPriceListItems={setUpdatedPriceListItems}
                        updatedPriceListItems={updatedPriceListItems}
                        productId={productId}
                        warning={warning}
                      />
                    )),
                  )}
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex items-center justify-between">
        <Button
          color={"tertiary"}
          onClick={() => {
            if (skipAttempts === 0) {
              setSkipAttempts(1);
              return setWarning(true);
            }
            closeDialog();
          }}
        >
          Skip for now
        </Button>
        <Button
          disabled={loading || productPriceListBatchCreateMutation.isPending}
          onClick={() => {
            if (!updatedPriceListItems?.length && skipAttempts === 0) {
              setSkipAttempts(1);
              return setWarning(true);
            }
            onConfirm();
          }}
        >
          Confirm
        </Button>
      </DialogCTA>
    </div>
  );
}
